import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import * as blockchyp from 'smg/blockchyp';
import { AxiosStatusCode } from 'smg/axios';
import * as models from 'smg/customer-api/models';
import { GetAxiosParamErrors, ShowError } from 'smg/errors';
import { ReportValidity, SetValueKeepingCaret } from 'smg/form';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat, ParseDollars } from 'smg/util';

import i18n from '../../i18n';
import { State } from '../state';
import { Payment, Split, SplitBanner, SplitEntry, SplitFieldError } from '../types';

import { BillingAddressForm } from './billing-address';
import { BindSetDefault, MountCreditCard } from './forms/credit';
import { EnsureCreditCard } from './forms/shared';
import { CardFormScope, CreatePaymentOption, PaymentOptions, PendingPaymentOptionID } from './options';
import { ApplyCardIssuer, ChargedPayment, EntryCard, EntryCardState, EntryLabel, PaymentCommitted, PaymentPlan, SplitCharging, SplitCommitted, SplitSettled, Unassigned } from './plan';

const CREDIT = models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT;
const EXP_RE = /^(0[1-9]|1[0-2])\/\d{2}$/;

// SetupSplit listens on the payment body so redraws keep the same handlers.
export function SetupSplit(state: State): void {
    const body = state.dom.$paymentBody;
    if (!body) {
        return;
    }
    body.addEventListener('change', e => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement) || target.id !== 'split-switch') {
            return;
        }
        if (state.phase === 'startingSplit') {
            return;
        }
        if (target.checked) {
            void startSplit(state);
            return;
        }
        void endSplit(state, false);
    });
    body.addEventListener('click', e => {
        const target = e.target;
        if (!(target instanceof Element)) {
            return;
        }
        const button = target.closest('button');
        if (!(button instanceof HTMLButtonElement) || !state.split) {
            return;
        }
        if (button.id === 'split-add-card') {
            addNewEntry(state);
            return;
        }
        if (button.id === 'split-continue') {
            void continueSplit(state);
            return;
        }
        if (button.id === 'split-different') {
            openChooser(state);
            return;
        }
        if (button.id === 'payment-option-back' && body.querySelector('#payment-type-cc') && state.split) {
            DrawSplit(state);
            return;
        }
        if (button.id === 'payment-option-type-confirm' && state.split) {
            chooseType(state, body);
            return;
        }
        const row = button.closest<HTMLElement>('[data-split-entry]');
        const entry = entryById(state, row?.dataset['splitEntry']);
        if (!entry || !row) {
            const saved = button.closest<HTMLElement>('[data-saved-id]');
            if (button.classList.contains('split-add-saved') && saved?.dataset['savedId']) {
                addSaved(state, saved.dataset['savedId']);
            }
            return;
        }
        if (button.classList.contains('split-fill')) {
            fillEntry(state, entry);
            return;
        }
        if (button.classList.contains('split-swap')) {
            entry.picking = !entry.picking;
            DrawSplit(state);
            return;
        }
        if (button.classList.contains('split-again')) {
            duplicateEntry(state, entry);
            return;
        }
        if (button.classList.contains('split-remove')) {
            removeEntry(state, entry);
        }
    });
    body.addEventListener('input', e => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement)) {
            return;
        }
        if (target.classList.contains('split-amount')) {
            const entry = entryById(state, target.closest<HTMLElement>('[data-split-entry]')?.dataset['splitEntry']);
            if (!entry || locked(state, entry)) {
                return;
            }
            entry.amountInput = target.value;
            entry.amount = ParseDollars(target.value);
            soften(entry);
            state.split?.entryErrors.delete(entry.id);
            livePatch(state);
            return;
        }
        if (target.classList.contains('cc-expiration')) {
            const entry = entryById(state, target.closest<HTMLElement>('[data-split-entry]')?.dataset['splitEntry']);
            if (!entry) {
                return;
            }
            applyExpiration(entry.payment, target);
            soften(entry);
            state.split?.entryErrors.delete(entry.id);
            livePatch(state);
        }
    });
    body.addEventListener('focusout', e => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement) || !target.classList.contains('split-amount')) {
            return;
        }
        const entry = entryById(state, target.closest<HTMLElement>('[data-split-entry]')?.dataset['splitEntry']);
        if (!entry || locked(state, entry)) {
            return;
        }
        if (entry.amount.gt(0)) {
            entry.amountInput = entry.amount.toFixed(2);
            target.value = entry.amountInput;
        }
    });
    body.addEventListener('change', e => {
        const target = e.target;
        if (!(target instanceof HTMLSelectElement) || !target.classList.contains('split-pick')) {
            return;
        }
        const entry = entryById(state, target.closest<HTMLElement>('[data-split-entry]')?.dataset['splitEntry']);
        if (!entry) {
            return;
        }
        pickCard(state, entry, target.value);
    });
}

export function MaybeMountSwitch(state: State): void {
    // Once money may have moved on this order (a single-card charge with no answer keeps its key for the retry),
    // the split stays out of reach until that charge is resolved.
    if (state.split || PaymentCommitted(state)) {
        return;
    }
    const body = state.dom.$paymentBody;
    if (!body || body.querySelector('#split-switch')) {
        return;
    }
    if (!body.querySelector('.cc-card-fields') && !body.querySelector('.split-payment-inner')) {
        return;
    }
    body.prepend(template.Clone('#split-switch-template'));
}

export function DrawSplit(state: State): void {
    const split = state.split;
    const body = state.dom.$paymentBody;
    if (!split || !body) {
        return;
    }
    const list = ensureSplitList(split, body);
    if (!list) {
        return;
    }
    const plan = PaymentPlan(state);
    const gap = Unassigned(plan);
    const held = SplitCommitted(split);
    paintBanner(list, state, plan);
    paintSwitch(list, held || state.phase === 'startingSplit', SplitCharging(state));
    const rows = list.querySelector('#split-payment-rows');
    if (!(rows instanceof HTMLElement)) {
        return;
    }
    reconcileRows(state, rows, gap);
    paintStatus(list, state, plan);
    const different = list.querySelector('#split-different');
    different?.classList.toggle('d-none', held);
    // A settled split takes no more cards; Retry only finishes the order.
    list.querySelector('#split-add-card')?.parentElement?.classList.toggle('d-none', SplitSettled(split));
    list.querySelectorAll<HTMLButtonElement>('#split-add-card, #split-continue, #split-different').forEach(button => {
        button.disabled = SplitCharging(state);
    });
    state.updatePaymentSummary();
    state.syncSplitSummary();
}

export async function PrepareEntries(state: State): Promise<boolean> {
    const split = state.split;
    if (!split) {
        return false;
    }
    split.entryErrors.clear();
    let fieldsOk = true;
    for (const entry of split.entries) {
        if (entry.status === 'paid' || entry.status === 'unknown') {
            continue;
        }
        const problems = validateEntry(state, entry);
        if (problems.amount || problems.exp) {
            split.entryErrors.set(entry.id, problems);
            fieldsOk = false;
        }
    }
    if (!fieldsOk) {
        split.notReady = true;
        DrawSplit(state);
        // The bad row can sit far above Continue; without this the click looks like it did nothing.
        const first = split.entries.find(entry => split.entryErrors.has(entry.id));
        const problems = first ? split.entryErrors.get(first.id) : undefined;
        const $field = first?.rowEl?.querySelector<HTMLInputElement>(problems?.amount ? '.split-amount' : '.cc-expiration');
        $field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        $field?.focus({ preventScroll: true });
        return false;
    }
    // Billing fields are required when the address is not the shipping address. The collapsed fieldset is disabled, and ReportValidity skips disabled fields.
    if (state.dom.$paymentBodyForm && !ReportValidity(state.dom.$paymentBodyForm)) {
        split.notReady = true;
        return false;
    }
    for (const entry of split.entries) {
        if (entry.status === 'paid' || entry.status === 'unknown') {
            continue;
        }
        if (EntryCardState(entry) !== 'draft') {
            continue;
        }
        try {
            await CreatePaymentOption(state, EntryScope(entry));
            // The mount is unloaded. The staged card lives on pendingPaymentOptionCreate, so the dead form must not stay on the row.
            entry.fieldsEl?.remove();
            entry.fieldsEl = undefined;
            entry.mount = undefined;
        } catch (err) {
            // A field problem is already painted on the card's own field; anything else (a network failure, a
            // mount that stopped answering) has no field to show it on.
            if (!(err instanceof blockchyp.TokenizeFieldError)) {
                ShowError(err);
            }
            split.notReady = true;
            DrawSplit(state);
            return false;
        }
    }
    // Ready, not just balanced: amounts can still change while cards tokenize, and a $0 row must not reach a charge.
    const plan = PaymentPlan(state);
    if (!plan.ready) {
        split.notReady = true;
        DrawSplit(state);
        return false;
    }
    split.notReady = false;
    split.totalChanged = false;
    for (const entry of split.entries) {
        entry.picking = false;
    }
    return true;
}

// ChargeOptionID is the payment option a row charges. A sameAs copy uses the source row's option.
export function ChargeOptionID(entries: SplitEntry[], entry: SplitEntry): string | undefined {
    const option = ChargedPayment(entries, entry).paymentOption;
    return option?.key ?? option?.id;
}

export function EntryScope(entry: SplitEntry): CardFormScope {
    return {
        payment: entry.payment,
        getMount: () => entry.mount,
        setMount: mount => {
            entry.mount = mount;
        },
        root: entry.rowEl ?? document,
    };
}

export function BeginSplitCharge(state: State): void {
    const split = state.split;
    if (!split) {
        return;
    }
    split.chargeTotal = split.entries.filter(entry => entry.status !== 'paid').length;
    split.chargeIndex = 0;
    for (const entry of split.entries) {
        if (entry.status !== 'paid') {
            entry.status = 'waiting';
        }
    }
    hideEdits();
    const body = state.dom.$paymentBody;
    if (body) {
        bootstrap.Collapse.getOrCreateInstance(body, { toggle: false }).hide();
    }
    state.updatePaymentSummary();
    state.syncSplitSummary();
}

// StopSplitRun puts a failed run back to editing so the customer can try again.
export function StopSplitRun(state: State): void {
    const split = state.split;
    if (!split) {
        return;
    }
    for (const entry of split.entries) {
        if (entry.status === 'waiting' || entry.status === 'charging') {
            entry.status = 'editing';
        }
    }
}

export function MarkEntryCharging(state: State, entry: SplitEntry, n: number): void {
    if (state.split) {
        state.split.chargeIndex = n;
    }
    entry.status = 'charging';
    state.updatePaymentSummary();
    state.syncSplitSummary();
}

export function ApplyPaid(entry: SplitEntry, orderPaymentInit: models.IOrderPayment): void {
    entry.status = 'paid';
    entry.reason = '';
    entry.orderPayment = new models.OrderPayment(orderPaymentInit);
    delete entry.idempotencyKey;
}

// DeclineReason is the processor sentence when the charge is a confirmed
// decline. Anything else stays unknown so the same idempotency key is retried.
export function DeclineReason(err: unknown): string | undefined {
    if (AxiosStatusCode(err) !== 400) {
        return undefined;
    }
    const paramErrors = GetAxiosParamErrors(err);
    if (!paramErrors) {
        return undefined;
    }
    for (const item of paramErrors.errors) {
        if (item.code === 'declined') {
            return item.message;
        }
    }
    return undefined;
}

export function FailEntry(state: State, entry: SplitEntry, kind: 'declined' | 'unknown', reason: string): void {
    entry.status = kind;
    entry.reason = kind === 'declined' ? reason : '';
    if (kind === 'declined') {
        delete entry.idempotencyKey;
    }
    let after = false;
    for (const other of state.split?.entries ?? []) {
        if (other === entry) {
            after = true;
            continue;
        }
        if (after && other.status !== 'paid') {
            other.status = 'not-tried';
        }
    }
}

// The persisted card is a saved card. A later charge uses that option, and the typed form is already unloaded.
export function SettleNewCard(entry: SplitEntry): void {
    entry.source = 'saved';
    entry.fieldsEl?.remove();
    entry.fieldsEl = undefined;
    entry.mount = undefined;
}

// An unconfirmed charge is the row the customer has to resolve, so it outranks a total change and a decline.
export function SplitBanner(split: Split): SplitBanner | undefined {
    if (split.entries.some(entry => entry.status === 'unknown')) {
        return 'unknown';
    }
    if (split.totalChanged) {
        return 'total';
    }
    if (split.entries.some(entry => entry.status === 'declined')) {
        if (split.entries.some(entry => entry.status === 'paid')) {
            return 'partial';
        }
        return 'first';
    }
    return undefined;
}

export function ReopenSplit(state: State): void {
    document.querySelectorAll('.accordion-header a[data-bs-toggle="collapse"]').forEach(link => {
        if (link instanceof HTMLAnchorElement) {
            link.classList.remove('d-none');
        }
    });
    if (state.orderResp) {
        state.dom.$shippingBodyToggle?.classList.add('d-none');
        if (!state.taxStepComplete) {
            state.dom.$taxBodyToggle?.classList.add('d-none');
        }
    }
    const body = state.dom.$paymentBody;
    if (body && !body.classList.contains('show')) {
        bootstrap.Collapse.getOrCreateInstance(body, { toggle: false }).show();
    }
    DrawSplit(state);
}

export function OnTotalMaybeChanged(state: State): void {
    const split = state.split;
    if (!split || SplitCharging(state)) {
        return;
    }
    if (!split.coveredTotal) {
        split.coveredTotal = state.totalAmount;
        return;
    }
    if (split.coveredTotal.eq(state.totalAmount)) {
        return;
    }
    split.coveredTotal = state.totalAmount;
    if (PaymentPlan(state).balanced) {
        return;
    }
    split.totalChanged = true;
    state.paymentStepComplete = false;
    state.dom.$authorizePaymentBtn.disabled = true;
    const body = state.dom.$paymentBody;
    if (body && !body.classList.contains('show')) {
        bootstrap.Collapse.getOrCreateInstance(body, { toggle: false }).show();
    }
    DrawSplit(state);
}

export async function StartSplitFromResume(state: State, orderInit: models.IResumableOrder): Promise<boolean> {
    const order = new models.ResumableOrder(orderInit);
    const payments = order.payments.filter(payment =>
        payment.paymentOptionTypeID === CREDIT && payment.total.gt(0));
    if (!payments.length) {
        return false;
    }
    let paidSum = Big(0);
    for (const payment of payments) {
        paidSum = paidSum.plus(payment.total);
    }
    const balance = order.balance.gt(0) ? order.balance : Big(0);
    state.resumeAuthoritativeTotal = paidSum.plus(balance);
    state.detailsComplete = true;
    const split = newSplit(state.resumeAuthoritativeTotal);
    for (const payment of payments) {
        split.entries.push(paidFromResume(split, payment));
    }
    // The paid rows come from the order, so a failed saved-card lookup must not stop the resume: the balance
    // goes on a new card row instead.
    split.saved = await loadSaved(state).catch((err: unknown) => {
        ShowError(err);
        return [];
    });
    const cards = split.saved;
    const usable = cards.find(card => !cardExpired(card));
    const editable = usable ? savedEntry(split, usable) : freshEntry(split);
    editable.amount = balance;
    editable.amountInput = balance.gt(0) ? balance.toFixed(2) : '';
    split.entries.push(editable);
    state.split = split;
    state.payment = editable.payment;
    return true;
}

async function startSplit(state: State): Promise<void> {
    if (state.split || state.phase !== 'editing' || PaymentCommitted(state)) {
        return;
    }
    const body = state.dom.$paymentBody;
    if (!body) {
        return;
    }
    const toggle = () => state.dom.$paymentBody?.querySelector<HTMLInputElement>('#split-switch');
    state.phase = 'startingSplit';
    const opening = toggle();
    if (opening) {
        opening.disabled = true;
    }
    try {
        // Saved cards load before the split, the single-card mount, or the payment step change. A failed load leaves that step as it was.
        let cards: models.PaymentOption[];
        try {
            cards = await loadSaved(state);
        } catch (err) {
            const input = toggle();
            if (input) {
                input.checked = false;
            }
            ShowError(err);
            return;
        }
        if (!toggle()?.checked) {
            return;
        }
        try {
            const fromNew = Boolean(body.querySelector('.cc-card-fields'));
            if (fromNew) {
                state.creditCardMount?.unload();
                state.creditCardMount = undefined;
            }
            const split = newSplit(state.totalAmount);
            split.saved = cards;
            let first: SplitEntry;
            if (fromNew) {
                first = freshEntry(split);
            } else {
                const checked = body.querySelector<HTMLInputElement>('.payment-option input[type="radio"]:checked');
                if (checked?.value === PendingPaymentOptionID && state.payment.pendingPaymentOptionCreate) {
                    // The selected card is staged, not on the account yet; the first row keeps it, token and all.
                    first = freshEntry(split);
                    first.payment = state.payment;
                } else {
                    const selected = cards.find(card => card.id === checked?.value) ?? cards.find(card => !cardExpired(card));
                    first = selected ? savedEntry(split, selected) : freshEntry(split);
                }
            }
            const firstId = first.payment.paymentOption?.id;
            const secondCard = cards.find(card => card.id && card.id !== firstId && !cardExpired(card));
            const second = secondCard ? savedEntry(split, secondCard) : freshEntry(split);
            split.entries = [first, second];
            state.split = split;
            state.payment = first.payment;
            DrawSplit(state);
        } catch (err) {
            ShowError(err);
            await endSplit(state, false);
        }
    } finally {
        state.phase = 'editing';
        const input = toggle();
        if (input) {
            input.disabled = false;
        }
    }
}

async function endSplit(state: State, announce: boolean): Promise<void> {
    const split = state.split;
    if (!split) {
        return;
    }
    if (SplitCommitted(split)) {
        const toggle = state.dom.$paymentBody?.querySelector<HTMLInputElement>('#split-switch');
        if (toggle) {
            toggle.checked = true;
        }
        return;
    }
    const keeper = split.entries.find(entry => entry.status !== 'paid');
    for (const entry of split.entries) {
        if (entry !== keeper) {
            entry.mount?.unload();
        }
    }
    if (keeper) {
        state.payment = keeper.payment;
        if (announce) {
            const card = EntryCard(keeper.payment);
            toast.Info(i18n('checkoutSplitOneLeft', {
                brand: card.brand,
                last4: card.last4,
                total: DollarFormat(state.totalAmount),
            }));
        }
        const pending = keeper.payment.paymentOption;
        if (pending?.id === PendingPaymentOptionID) {
            state.userPaymentOptions.add(pending);
        }
        keeper.mount?.unload();
    }
    state.creditCardMount = undefined;
    state.split = undefined;
    await state.initPayment();
}

async function continueSplit(state: State): Promise<void> {
    if (SplitCharging(state)) {
        return;
    }
    if (!await PrepareEntries(state)) {
        return;
    }
    state.paymentStepComplete = true;
    DrawSplit(state);
    if (state.orderResp || state.detailsComplete) {
        state.syncSplitSummary();
        return;
    }
    state.dom.$additionalBodyToggle?.click();
}

function addNewEntry(state: State): void {
    const split = state.split;
    if (!split || SplitCharging(state) || SplitSettled(split)) {
        return;
    }
    split.entries.push(freshEntry(split));
    DrawSplit(state);
}

function addSaved(state: State, id: string): void {
    const split = state.split;
    if (!split || SplitCharging(state) || SplitSettled(split)) {
        return;
    }
    const card = [...state.userPaymentOptions].find(option => option.id === id);
    if (!card) {
        return;
    }
    split.entries.push(savedEntry(split, card));
    DrawSplit(state);
}

function duplicateEntry(state: State, entry: SplitEntry): void {
    const split = state.split;
    if (!split || locked(state, entry) || entry.status === 'declined') {
        return;
    }
    const copy = freshEntry(split);
    if (EntryCardState(entry) === 'saved' && entry.payment.paymentOption) {
        copy.source = 'saved';
        copy.payment = {
            billingShippingSame: true,
            paymentOptionTypeID: entry.payment.paymentOption.paymentOptionTypeID,
            paymentOption: entry.payment.paymentOption,
        };
    } else {
        copy.source = 'new';
        copy.sameAs = entry.sameAs || entry.id;
        copy.payment = { billingShippingSame: true, paymentOptionTypeID: CREDIT };
    }
    const index = split.entries.indexOf(entry);
    split.entries.splice(index + 1, 0, copy);
    DrawSplit(state);
}

function removeEntry(state: State, entry: SplitEntry): void {
    const split = state.split;
    if (!split || !removable(state, entry)) {
        return;
    }
    releaseCopies(split, entry.id);
    entry.mount?.unload();
    entry.mount = undefined;
    entry.fieldsEl = undefined;
    entry.rowEl?.remove();
    entry.rowEl = undefined;
    split.entryErrors.delete(entry.id);
    split.entries = split.entries.filter(item => item !== entry);
    const paid = split.entries.some(item => item.status === 'paid');
    const unpaid = split.entries.filter(item => item.status !== 'paid');
    if (!SplitCommitted(split) && split.entries.length <= 1) {
        void endSplit(state, Boolean(keeperStill(split)));
        return;
    }
    if (paid && unpaid.length === 0) {
        const added = freshEntry(split);
        const due = PaymentPlan(state).left;
        added.amount = due;
        added.amountInput = due.gt(0) ? due.toFixed(2) : '';
        split.entries.push(added);
    }
    DrawSplit(state);
}

function keeperStill(split: Split): boolean {
    return split.entries.length === 1;
}

function fillEntry(state: State, entry: SplitEntry): void {
    if (locked(state, entry)) {
        return;
    }
    const gap = Unassigned(PaymentPlan(state));
    entry.amount = entry.amount.plus(gap);
    entry.amountInput = entry.amount.toFixed(2);
    soften(entry);
    state.split?.entryErrors.delete(entry.id);
    const input = entry.rowEl?.querySelector<HTMLInputElement>('.split-amount');
    if (input) {
        input.value = entry.amountInput;
    }
    livePatch(state);
}

function pickCard(state: State, entry: SplitEntry, value: string): void {
    if (locked(state, entry)) {
        return;
    }
    entry.picking = false;
    delete entry.sameAs;
    entry.mount?.unload();
    entry.mount = undefined;
    entry.fieldsEl?.remove();
    entry.fieldsEl = undefined;
    soften(entry);
    if (value === 'new') {
        entry.source = 'new';
        entry.payment = { billingShippingSame: true, paymentOptionTypeID: CREDIT };
    } else {
        const card = [...state.userPaymentOptions].find(option => option.id === value);
        if (!card) {
            DrawSplit(state);
            return;
        }
        entry.source = 'saved';
        entry.payment = {
            billingShippingSame: true,
            paymentOptionTypeID: card.paymentOptionTypeID,
            paymentOption: card,
        };
    }
    DrawSplit(state);
}

function openChooser(state: State): void {
    const body = state.dom.$paymentBody;
    const split = state.split;
    if (!body || !split || SplitCharging(state) || SplitCommitted(split)) {
        return;
    }
    split.listEl?.classList.add('d-none');
    body.appendChild(template.Clone('#payment-option-form-new-payment'));
    body.appendChild(template.Clone('#payment-options-nav-new-po-select-template'));
}

function chooseType(state: State, body: HTMLElement): void {
    const selected = body.querySelector<HTMLInputElement>('input[type="radio"]:checked');
    const kind = selected?.dataset['paymentType'] || '';
    if (kind === 'credit-debit') {
        addNewEntry(state);
        return;
    }
    const typeID = typeFromKind(kind);
    if (!typeID) {
        return;
    }
    const frag = template.Clone('#split-ends-body');
    const box = frag.querySelector('div');
    const list = frag.querySelector('.split-ends-cards');
    for (const entry of state.split?.entries ?? []) {
        const item = document.createElement('li');
        item.className = 'd-flex align-items-center gap-2';
        const icon = document.createElement('span');
        icon.className = 'material-symbols-rounded text-primary';
        icon.textContent = 'credit_card';
        item.append(icon, document.createTextNode(EntryLabel(shownPayment(state, entry))));
        list?.appendChild(item);
    }
    if (!(box instanceof HTMLElement)) {
        return;
    }
    new Modal({
        title: i18n('checkoutSplitEndsTitle'),
        body: box,
        confirmText: i18n('checkoutSplitEndsConfirm'),
        cancelText: i18n('checkoutSplitEndsCancel'),
        confirmButtonClass: 'btn btn-primary',
        cancelButtonClass: 'btn btn-outline-primary',
        onSubmit: () => {
            for (const entry of state.split?.entries ?? []) {
                entry.mount?.unload();
            }
            state.split = undefined;
            state.creditCardMount = undefined;
            state.payment = { billingShippingSame: true, paymentOptionTypeID: typeID };
            state.promptNewPaymentOptionByType(typeID, state.dom.$paymentBody ?? undefined);
        },
    });
}

function typeFromKind(kind: string): models.PaymentOptionTypeID | undefined {
    switch (kind) {
    case 'paypal':
        return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
    case 'purchase-order':
        return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PO;
    case 'check':
        return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CHECK;
    case 'wire':
        return models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_WIRE;
    default:
        return undefined;
    }
}

function ensureSplitList(split: Split, body: HTMLElement): HTMLElement | undefined {
    let list = split.listEl;
    if (!(list instanceof HTMLElement)) {
        const created = template.Clone('#split-body-template').querySelector('.split-payment-list');
        if (!(created instanceof HTMLElement)) {
            return undefined;
        }
        list = created;
        split.listEl = list;
    }
    for (const child of [...body.children]) {
        if (child !== list) {
            child.remove();
        }
    }
    if (list.parentElement !== body) {
        body.appendChild(list);
    }
    list.classList.remove('d-none');
    return list;
}

function paintSwitch(list: HTMLElement, paidAny: boolean, charging: boolean): void {
    const slot = list.querySelector('#split-switch-slot');
    if (!slot) {
        return;
    }
    if (!slot.querySelector('#split-switch')) {
        slot.appendChild(template.Clone('#split-switch-template'));
    }
    const toggle = slot.querySelector<HTMLInputElement>('#split-switch');
    if (!toggle) {
        return;
    }
    toggle.checked = true;
    toggle.disabled = paidAny || charging;
}

function reconcileRows(state: State, rows: HTMLElement, gap: Big): void {
    const split = state.split;
    if (!split) {
        return;
    }
    const live = new Set(split.entries.map(entry => entry.rowEl).filter((row): row is HTMLElement => row instanceof HTMLElement));
    for (const row of [...rows.querySelectorAll<HTMLElement>(':scope > .split-payment[data-split-entry]')]) {
        if (!live.has(row)) {
            row.remove();
        }
    }
    const unpaid = split.entries.filter(entry => entry.status !== 'paid').length;
    for (let i = 0; i < split.entries.length; i++) {
        const entry = split.entries[i];
        let row = entry.rowEl;
        if (!(row instanceof HTMLElement) || !row.isConnected) {
            row = createEntryRow(entry);
            entry.rowEl = row;
            const next = split.entries.slice(i + 1).map(item => item.rowEl).find(item => item?.parentElement === rows);
            placeRow(rows, row, next);
        }
        updateEntryRow(state, entry, row, gap, unpaid);
    }
    const used = new Set(split.entries.filter(entry => EntryCardState(entry) === 'saved').map(entry => entry.payment.paymentOption?.id || ''));
    const unusedCards = SplitSettled(split) ? [] : split.saved.filter(card => card.id && !used.has(card.id));
    const unused = unusedCards.map(card => renderUnused(card, card === split.saved[0]));
    rows.querySelectorAll(':scope > .split-payment-unused').forEach(node => node.remove());
    rows.append(...unused);
}

function placeRow(rows: HTMLElement, row: HTMLElement, next: HTMLElement | undefined): void {
    if (row.parentElement === rows) {
        return;
    }
    if (next?.parentElement === rows) {
        rows.insertBefore(row, next);
        return;
    }
    const unused = rows.querySelector('.split-payment-unused');
    rows.insertBefore(row, unused);
}

function createEntryRow(entry: SplitEntry): HTMLElement {
    const frag = template.Clone('#split-row-template');
    const row = frag.querySelector('.split-payment');
    if (!(row instanceof HTMLElement)) {
        return document.createElement('div');
    }
    row.dataset['splitEntry'] = entry.id;
    const input = row.querySelector<HTMLInputElement>('.split-amount');
    const label = row.querySelector('.split-amount-label');
    if (label instanceof HTMLLabelElement && input) {
        input.id = `split-amount-${entry.id}`;
        label.htmlFor = input.id;
    }
    return row;
}

function updateEntryRow(state: State, entry: SplitEntry, row: HTMLElement, gap: Big, unpaid: number): void {
    const split = state.split;
    row.dataset['status'] = entry.status;
    const problems = split?.entryErrors.get(entry.id);
    const card = row.querySelector(':scope > .split-payment-row .payment-option');
    if (card instanceof HTMLElement) {
        card.classList.toggle('is-invalid', entry.status === 'declined' || Boolean(problems?.amount));
    }
    const nameSlot = row.querySelector('.split-payment-card');
    if (nameSlot instanceof HTMLElement) {
        paintName(state, entry, nameSlot, split?.saved ?? []);
    }
    const input = row.querySelector<HTMLInputElement>('.split-amount');
    if (input) {
        SetValueKeepingCaret(input, entry.amountInput);
        const showLock = entry.status === 'paid' || entry.status === 'unknown';
        const isLocked = locked(state, entry);
        input.readOnly = showLock;
        input.disabled = isLocked && !showLock;
        input.classList.toggle('is-invalid', Boolean(problems?.amount));
        const feedback = row.querySelector('.split-amount-error');
        if (feedback) {
            feedback.textContent = problems?.amount || '';
        }
    }
    const lock = row.querySelector('.split-amount-lock');
    const showLock = entry.status === 'paid' || entry.status === 'unknown';
    lock?.classList.toggle('d-none', !showLock);
    const lockText = lock?.querySelector('.visually-hidden');
    if (lockText) {
        lockText.textContent = entry.status === 'unknown' ? i18n('checkoutSplitUnknownTag') : i18n('checkoutSplitAmountLocked');
    }
    const fill = row.querySelector('.split-fill');
    if (fill instanceof HTMLButtonElement) {
        const show = !locked(state, entry) && gap.gt(0);
        fill.classList.toggle('d-none', !show);
        fill.textContent = i18n('checkoutSplitFill', { amount: DollarFormat(entry.amount.plus(gap)) });
    }
    paintActions(row, entry, state, unpaid);
    mountNewFields(state, entry, row);
}

function paintName(state: State, entry: SplitEntry, nameSlot: HTMLElement, cards: models.PaymentOption[]): void {
    const wantPick = entry.picking && !locked(state, entry);
    if (wantPick) {
        if (!nameSlot.querySelector('.split-pick')) {
            nameSlot.replaceChildren(renderPicker(entry, cards));
            nameSlot.querySelector<HTMLSelectElement>('.split-pick')?.focus();
        }
        return;
    }
    const name = renderName(shownEntry(state, entry));
    const tags = renderTags(entry, cards);
    if (tags) {
        nameSlot.replaceChildren(name, tags);
        return;
    }
    nameSlot.replaceChildren(name);
}

function renderUnused(card: models.PaymentOption, lastUsed: boolean): HTMLElement {
    const frag = template.Clone('#split-unused-template');
    const row = frag.querySelector('.split-payment');
    if (!(row instanceof HTMLElement)) {
        return document.createElement('div');
    }
    row.dataset['savedId'] = card.id;
    const nameSlot = row.querySelector('.split-payment-card');
    nameSlot?.appendChild(renderSavedName(card));
    const tags = document.createElement('div');
    tags.className = 'd-flex flex-wrap gap-2 align-items-center';
    if (lastUsed) {
        tags.appendChild(passiveTag(i18n('checkoutSplitLastUsed'), false));
    }
    if (cardExpired(card)) {
        tags.appendChild(passiveTag(i18n('checkoutSplitExpired'), true));
        row.querySelector('.split-add-saved')?.remove();
    }
    if (tags.childElementCount) {
        nameSlot?.appendChild(tags);
    }
    return row;
}

function renderName(entry: SplitEntry): HTMLElement {
    const option = entry.payment.paymentOption;
    if (option?.creditCardLast4 || option?.creditCardFirst6) {
        return renderSavedName(option);
    }
    const pending = entry.payment.pendingPaymentOptionCreate;
    if (pending?.creditCardLast4) {
        const standIn = new models.PaymentOption({
            creditCardFirst6: pending.creditCardFirst6,
            creditCardLast4: pending.creditCardLast4,
        });
        return renderSavedName(standIn);
    }
    const wrap = document.createElement('div');
    wrap.className = 'payment-option-name mt-1';
    wrap.textContent = i18n('checkoutSplitNewCard');
    return wrap;
}

function renderSavedName(option: models.PaymentOption): HTMLElement {
    const frag = template.Clone('#payment-option-card-cc');
    const name = frag.querySelector('.payment-option-name');
    const node = name instanceof HTMLElement ? name : document.createElement('div');
    const last4 = option.creditCardLast4?.toString().padStart(4, '0') || '';
    const last = node.querySelector('.cc-last-four');
    if (last) {
        last.textContent = last4;
    }
    ApplyCardIssuer(node, option.creditCardFirst6?.toString() || '');
    const address = node.querySelector('.cc-address');
    const line = option.address.toLocalityLine().trim();
    if (address && line) {
        address.textContent = line;
    } else {
        address?.remove();
    }
    return node;
}

function renderPicker(entry: SplitEntry, cards: models.PaymentOption[]): HTMLElement {
    const wrap = document.createElement('div');
    const label = document.createElement('label');
    label.className = 'form-label small mb-1';
    label.textContent = i18n('checkoutSplitPickLabel');
    const select = document.createElement('select');
    select.className = 'form-select form-select-lg split-pick';
    // The current card shows first but cannot be picked, so choosing any real option, the new-card one
    // included, always fires change.
    const current = document.createElement('option');
    current.value = '';
    current.textContent = EntryLabel(entry.payment);
    current.disabled = true;
    current.selected = true;
    select.appendChild(current);
    for (const card of cards) {
        const option = document.createElement('option');
        option.value = card.id;
        const brand = EntryCard({ paymentOption: card }).brand;
        const last4 = card.creditCardLast4?.toString().padStart(4, '0') || '';
        option.textContent = `${brand} · · · · ${last4}`;
        if (cardExpired(card)) {
            option.disabled = true;
            option.textContent += ` — ${i18n('checkoutSplitExpired')}`;
        }
        select.appendChild(option);
    }
    const fresh = document.createElement('option');
    fresh.value = 'new';
    fresh.textContent = i18n('checkoutSplitNewCardPick');
    select.appendChild(fresh);
    label.htmlFor = `split-pick-${entry.id}`;
    select.id = label.htmlFor;
    wrap.append(label, select);
    return wrap;
}

function renderTags(entry: SplitEntry, cards: models.PaymentOption[]): HTMLElement | undefined {
    const tags = document.createElement('div');
    tags.className = 'd-flex flex-wrap gap-2 align-items-center';
    const option = entry.payment.paymentOption;
    if (option?.id && cards[0]?.id === option.id) {
        tags.appendChild(passiveTag(i18n('checkoutSplitLastUsed'), false));
    }
    if (option && cardExpired(option)) {
        tags.appendChild(passiveTag(i18n('checkoutSplitExpired'), true));
    }
    if (entry.status === 'paid') {
        const tag = document.createElement('span');
        tag.className = 'tag tag-soft tag-upper tag-xs tag-success d-inline-flex mb-0';
        tag.append(icon('check'), document.createTextNode(i18n('checkoutSplitPaid')));
        tags.appendChild(tag);
    } else if (entry.status === 'declined') {
        const tag = document.createElement('span');
        tag.className = 'tag tag-soft tag-danger d-inline-flex mb-0 text-wrap text-start';
        tag.setAttribute('role', 'alert');
        tag.append(icon('error'), document.createTextNode(i18n('checkoutSplitDeclined', { reason: entry.reason })));
        tags.appendChild(tag);
    } else if (entry.status === 'unknown') {
        const tag = document.createElement('span');
        tag.className = 'tag tag-soft tag-upper tag-xs tag-warning d-inline-flex mb-0';
        tag.textContent = i18n('checkoutSplitUnknownTag');
        tags.appendChild(tag);
    } else if (entry.status === 'not-tried') {
        const note = document.createElement('span');
        note.className = 'small text-body-secondary';
        note.textContent = i18n('checkoutSplitNotTried');
        tags.appendChild(note);
    }
    return tags.childElementCount ? tags : undefined;
}

function paintActions(row: HTMLElement, entry: SplitEntry, state: State, unpaid?: number): void {
    const count = unpaid ?? state.split?.entries.filter(item => item.status !== 'paid').length ?? 0;
    const swap = row.querySelector('.split-swap');
    const again = row.querySelector('.split-again');
    const remove = row.querySelector('.split-remove');
    const isLocked = locked(state, entry);
    const hideAll = entry.status === 'paid' || entry.status === 'unknown';
    if (swap instanceof HTMLButtonElement) {
        swap.classList.toggle('d-none', hideAll);
        swap.disabled = isLocked;
        swap.classList.toggle('btn-sm', entry.status === 'declined');
        swap.classList.toggle('btn-outline-primary', entry.status === 'declined');
        swap.classList.toggle('btn-xs', entry.status !== 'declined');
        swap.classList.toggle('btn-link', entry.status !== 'declined');
    }
    if (again instanceof HTMLButtonElement) {
        again.classList.toggle('d-none', hideAll || entry.status === 'declined');
        again.disabled = isLocked;
    }
    if (remove instanceof HTMLButtonElement) {
        const show = !hideAll && (count > 1 || entry.status === 'declined');
        remove.classList.toggle('d-none', !show);
        remove.disabled = isLocked;
        const card = EntryCard(shownPayment(state, entry));
        const removeLabel = card.last4 ?
            i18n('checkoutSplitRemoveCardLabel', { brand: card.brand, last4: card.last4 }) :
            i18n('checkoutSplitRemoveCard');
        remove.setAttribute('aria-label', removeLabel);
    }
}

function mountNewFields(state: State, entry: SplitEntry, row: HTMLElement): void {
    const slot = row.querySelector('.split-new-card-slot');
    if (!(slot instanceof HTMLElement)) {
        return;
    }
    const show = EntryCardState(entry) === 'draft' && entry.status !== 'paid' && entry.status !== 'unknown';
    slot.classList.toggle('d-none', !show);
    if (!show) {
        return;
    }
    // A tokenize timeout unloads the mount and removes its iframes, so that form can never take the card again.
    if (entry.fieldsEl && entry.mount?.isUnloaded) {
        entry.fieldsEl.remove();
        entry.fieldsEl = undefined;
        entry.mount = undefined;
    }
    if (entry.fieldsEl && slot.contains(entry.fieldsEl)) {
        return;
    }
    if (!entry.fieldsEl) {
        const frag = template.Clone('#split-new-fields');
        const fields = frag.querySelector('.payment-option-form-body');
        if (!(fields instanceof HTMLElement)) {
            return;
        }
        html.IDsToClasses(fields);
        entry.fieldsEl = fields;
        slot.appendChild(fields);
        state.initCCInputs(fields);
        entry.payment.billingAddress = new BillingAddressForm(state, fields);
        entry.payment.paymentOptionTypeID = CREDIT;
        EnsureCreditCard(entry.payment);
        entry.mount = MountCreditCard(state, fields, { keep: true });
        fields.querySelector<HTMLInputElement>('.cc-expiration')?.addEventListener('input', () => {
            const input = fields.querySelector<HTMLInputElement>('.cc-expiration');
            if (input) {
                applyExpiration(entry.payment, input);
            }
        });
        BindSetDefault(entry.payment, fields);
        return;
    }
    slot.appendChild(entry.fieldsEl);
}

function paintBanner(root: ParentNode, state: State, plan = PaymentPlan(state)): void {
    const slot = root.querySelector('#split-payment-banner');
    if (!slot || !state.split) {
        return;
    }
    slot.replaceChildren();
    const banner = SplitBanner(state.split);
    if (banner === 'unknown') {
        const entry = state.split.entries.find(item => item.status === 'unknown');
        const frag = template.Clone('#split-banner-unknown');
        const text = frag.querySelector('.split-banner-text');
        if (text && entry) {
            const card = EntryCard(ChargedPayment(state.split.entries, entry));
            text.textContent = i18n('checkoutSplitUnknown', { brand: card.brand, last4: card.last4 });
        }
        slot.appendChild(frag);
    } else if (banner === 'partial') {
        const frag = template.Clone('#split-banner-partial');
        const text = frag.querySelector('.split-banner-text');
        if (text) {
            text.textContent = i18n('checkoutSplitPartialBody', {
                paid: DollarFormat(plan.paid),
                left: DollarFormat(plan.left),
            });
        }
        slot.appendChild(frag);
    } else if (banner === 'first') {
        slot.appendChild(template.Clone('#split-banner-first'));
    } else if (banner === 'total') {
        const frag = template.Clone('#split-banner-total');
        const text = frag.querySelector('.split-banner-text');
        if (text) {
            text.textContent = i18n('checkoutSplitTotalChanged', { total: DollarFormat(plan.cover) });
        }
        slot.appendChild(frag);
    }
}

function paintStatus(root: ParentNode, state: State, plan = PaymentPlan(state)): void {
    const slot = root.querySelector('#split-payment-status');
    if (!slot) {
        return;
    }
    const gap = Unassigned(plan);
    let tone = 'info';
    if (plan.balanced) {
        tone = 'success';
    } else if (state.split?.notReady) {
        tone = 'danger';
    } else if (plan.over.gt(0)) {
        tone = 'warning';
    }
    let message = i18n('checkoutSplitLeft', { amount: DollarFormat(gap) });
    if (plan.balanced) {
        message = i18n('checkoutSplitBalanced');
    } else if (plan.over.gt(0)) {
        message = i18n('checkoutSplitOver', { over: DollarFormat(plan.over) });
    }
    const pct = plan.cover.gt(0) ? Math.min(100, Math.round(plan.assigned.div(plan.cover).times(100).toNumber())) : 0;
    const width = plan.over.gt(0) ? 100 : pct;
    let barTone = '';
    if (plan.over.gt(0)) {
        barTone = ' bg-danger';
    } else if (plan.balanced) {
        barTone = ' bg-success';
    }
    slot.replaceChildren();
    const block = document.createElement('div');
    block.className = 'split-payment-status d-flex flex-column gap-2';
    const figures = document.createElement('div');
    figures.className = 'd-flex flex-wrap justify-content-between gap-2 small';
    const onCards = document.createElement('span');
    onCards.className = 'text-nowrap';
    onCards.append(document.createTextNode(`${i18n('checkoutSplitOnCards')} `), bold(DollarFormat(plan.assigned)));
    const orderTotal = document.createElement('span');
    orderTotal.className = 'text-nowrap';
    orderTotal.append(document.createTextNode(`${i18n('checkoutSplitOrderTotal')} `), bold(DollarFormat(plan.cover)));
    figures.append(onCards, orderTotal);
    const progress = document.createElement('div');
    progress.className = 'progress';
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-label', i18n('checkoutSplitOnCards'));
    progress.setAttribute('aria-valuenow', String(pct));
    progress.setAttribute('aria-valuemin', '0');
    progress.setAttribute('aria-valuemax', '100');
    const bar = document.createElement('div');
    bar.className = `progress-bar${barTone}`;
    bar.style.width = `${width}%`;
    progress.appendChild(bar);
    const alert = document.createElement('div');
    alert.className = `alert alert-sm alert-${tone} mb-0 mt-2`;
    alert.setAttribute('role', 'status');
    const copy = document.createElement('div');
    if (plan.balanced) {
        copy.className = 'fw-700';
    }
    if (state.split?.notReady && !plan.balanced) {
        const lead = document.createElement('b');
        lead.className = 'd-block';
        lead.textContent = i18n('checkoutSplitNotReady');
        copy.appendChild(lead);
    }
    copy.append(document.createTextNode(message));
    alert.appendChild(copy);
    block.append(figures, progress, alert);
    slot.appendChild(block);
}

function livePatch(state: State): void {
    const root = state.dom.$paymentBody;
    const split = state.split;
    if (!root || !split) {
        return;
    }
    const plan = PaymentPlan(state);
    const gap = Unassigned(plan);
    paintBanner(root, state, plan);
    paintStatus(root, state, plan);
    for (const entry of split.entries) {
        const row = entry.rowEl;
        if (!row?.isConnected) {
            continue;
        }
        const fill = row.querySelector('.split-fill');
        if (fill instanceof HTMLButtonElement) {
            const show = !locked(state, entry) && gap.gt(0);
            fill.classList.toggle('d-none', !show);
            fill.textContent = i18n('checkoutSplitFill', { amount: DollarFormat(entry.amount.plus(gap)) });
        }
        const input = row.querySelector('.split-amount');
        input?.classList.remove('is-invalid');
        row.querySelector('.payment-option')?.classList.remove('is-invalid');
        const feedback = row.querySelector('.split-amount-error');
        if (feedback) {
            feedback.textContent = '';
        }
        const nameSlot = row.querySelector('.split-payment-card');
        if (nameSlot && !entry.picking) {
            const tags = nameSlot.querySelector('.d-flex.flex-wrap.gap-2');
            tags?.remove();
            const next = renderTags(entry, state.split?.saved ?? []);
            if (next) {
                nameSlot.appendChild(next);
            }
        }
    }
    state.updatePaymentSummary();
    state.syncSplitSummary();
}

function validateEntry(state: State, entry: SplitEntry): SplitFieldError {
    const problems: SplitFieldError = {};
    if (!entry.amount.gt(0)) {
        problems.amount = i18n('checkoutSplitAmountZero');
    } else if (entry.amount.gt(state.totalAmount)) {
        problems.amount = i18n('checkoutSplitAmountOver', { total: DollarFormat(state.totalAmount) });
    }
    if (EntryCardState(entry) === 'draft') {
        const exp = entry.rowEl?.querySelector<HTMLInputElement>('.cc-expiration')?.value || '';
        if (!EXP_RE.test(exp)) {
            problems.exp = i18n('checkoutSplitExpMissing');
            const input = entry.rowEl?.querySelector<HTMLInputElement>('.cc-expiration');
            input?.setCustomValidity(problems.exp);
        }
    }
    return problems;
}

function newSplit(covered?: Big): Split {
    return {
        entries: [],
        totalChanged: false,
        notReady: false,
        seq: 0,
        chargeIndex: 0,
        coveredTotal: covered,
        saved: [],
        entryErrors: new Map(),
        chargeTotal: 0,
    };
}

function shownEntry(state: State, entry: SplitEntry): SplitEntry {
    if (!entry.sameAs || !state.split) {
        return entry;
    }
    return state.split.entries.find(item => item.id === entry.sameAs) ?? entry;
}

function shownPayment(state: State, entry: SplitEntry): Payment {
    return ChargedPayment(state.split?.entries ?? [], entry);
}

function releaseCopies(split: Split, sourceID: string): void {
    for (const other of split.entries) {
        if (other.sameAs !== sourceID) {
            continue;
        }
        delete other.sameAs;
        other.source = 'new';
        other.payment = { billingShippingSame: true, paymentOptionTypeID: CREDIT };
    }
}

function freshEntry(split: Split): SplitEntry {
    split.seq += 1;
    return {
        id: `s${split.seq}`,
        source: 'new',
        payment: { billingShippingSame: true, paymentOptionTypeID: CREDIT },
        amount: Big(0),
        amountInput: '',
        status: 'editing',
        reason: '',
        picking: false,
    };
}

function savedEntry(split: Split, card: models.PaymentOption): SplitEntry {
    const entry = freshEntry(split);
    entry.source = 'saved';
    entry.payment = {
        billingShippingSame: true,
        paymentOptionTypeID: card.paymentOptionTypeID,
        paymentOption: card,
    };
    return entry;
}

function paidFromResume(split: Split, payment: models.ResumablePayment): SplitEntry {
    const entry = freshEntry(split);
    entry.source = 'saved';
    entry.status = 'paid';
    entry.amount = payment.total;
    entry.amountInput = payment.total.toFixed(2);
    entry.payment = {
        billingShippingSame: true,
        paymentOptionTypeID: CREDIT,
        paymentOption: new models.PaymentOption({
            id: payment.paymentOptionID,
            paymentOptionTypeID: CREDIT,
            creditCardFirst6: payment.creditCardFirst6,
            creditCardLast4: payment.creditCardLast4,
        }),
    };
    return entry;
}

async function loadSaved(state: State): Promise<models.PaymentOption[]> {
    const cards: models.PaymentOption[] = [];
    const seen = new Set<string>();
    const add = (option: models.PaymentOption | undefined) => {
        if (!option?.id || option.paymentOptionTypeID !== CREDIT || seen.has(option.id) || option.id === PendingPaymentOptionID) {
            return;
        }
        seen.add(option.id);
        cards.push(option);
        state.userPaymentOptions.add(option);
    };
    if (state.sess?.user.id) {
        for (let page = 0; page < 20; page++) {
            const resp = await PaymentOptions(page);
            resp.paymentOptions.forEach(add);
            if (!resp.nextPage) {
                break;
            }
        }
        return cards;
    }
    state.userPaymentOptions.forEach(add);
    add(state.payment.paymentOption);
    return cards;
}

function locked(state: State, entry: SplitEntry): boolean {
    if (SplitCharging(state)) {
        return true;
    }
    return entry.status === 'paid' || entry.status === 'unknown' || entry.status === 'charging' || entry.status === 'waiting';
}

function removable(state: State, entry: SplitEntry): boolean {
    if (locked(state, entry) || entry.status === 'paid') {
        return false;
    }
    const unpaid = state.split?.entries.filter(item => item.status !== 'paid').length ?? 0;
    return unpaid > 1 || entry.status === 'declined';
}

function soften(entry: SplitEntry): void {
    if (entry.status === 'declined' || entry.status === 'not-tried') {
        entry.status = 'editing';
        entry.reason = '';
    }
}

function entryById(state: State, id: string | undefined): SplitEntry | undefined {
    if (!id) {
        return undefined;
    }
    return state.split?.entries.find(entry => entry.id === id);
}

function cardExpired(option: models.PaymentOption): boolean {
    const raw = option.creditCardExpirationDate || '';
    const match = /^(\d{1,2})\s*\/\s*(\d{2,4})$/.exec(raw);
    let month: number;
    let year: number;
    if (match) {
        month = Number(match[1]);
        year = Number(match[2]);
        if (year < 100) {
            year += 2000;
        }
    } else if (raw) {
        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) {
            return false;
        }
        month = parsed.getMonth() + 1;
        year = parsed.getFullYear();
    } else {
        return false;
    }
    const now = new Date();
    return year * 12 + month < now.getFullYear() * 12 + (now.getMonth() + 1);
}

function applyExpiration(payment: Payment, input: HTMLInputElement): void {
    const card = EnsureCreditCard(payment);
    const [monthRaw, yearRaw] = input.value.split('/');
    card.expireMonth = Number(monthRaw) || 0;
    card.expireYear = Number(yearRaw) || 0;
    if (EXP_RE.test(input.value)) {
        input.setCustomValidity('');
    }
}

function passiveTag(text: string, expired: boolean): HTMLElement {
    const tag = document.createElement('span');
    tag.className = expired ?
        'tag tag-passive expired tag-danger d-inline-flex mb-0' :
        'tag tag-passive last-used d-inline-flex mb-0';
    if (expired) {
        tag.append(icon('error'), document.createTextNode(text));
        return tag;
    }
    tag.textContent = text;
    return tag;
}

function icon(name: string): HTMLElement {
    const span = document.createElement('span');
    span.className = 'material-symbols-rounded me-1 fs-5';
    span.textContent = name;
    return span;
}

function bold(text: string): HTMLElement {
    const node = document.createElement('b');
    node.textContent = text;
    return node;
}

function hideEdits(): void {
    document.querySelectorAll('.accordion-header a[data-bs-toggle="collapse"]').forEach(link => {
        if (link instanceof HTMLAnchorElement) {
            link.classList.add('d-none');
        }
    });
}

export function ChargingNotice(state: State): string {
    return i18n('checkoutSplitCharging', {
        n: String(state.split?.chargeIndex || 1),
        count: String(state.split?.chargeTotal || 1),
    });
}
